// src/screens/WalletScreen.js
// ============================================================================
// UNIFIED WALLET SCREEN
// 
// Central hub for ALL fund management:
// - View balances (wallet + gift + held)
// - Deposit funds (Stripe Checkout)
// - Withdraw funds (Standard ACH / Turbo Pay)
// - Gift Cards (quick access)
// - Transaction history
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  Modal,
  Pressable,
  Image,
  Platform,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import useCritsEnabled from '../hooks/useCritsEnabled';
import useMembershipsLive from '../hooks/useMembershipsLive';
import { COUNTRY_OPTIONS } from '../constants/countries';

// Icon imports - using @expo/vector-icons (built into Expo)
import { MaterialCommunityIcons, Ionicons, Feather } from '@expo/vector-icons';

// ============================================================================
// CONSTANTS
// ============================================================================

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  GREEN: '#2ECC71',
  TEXT: '#EDEDED',
  DIM: '#888',
  DANGER: '#E74C3C',
  BLUE: '#3498DB',
  TURBO: '#9B59B6',
  GIFT: '#E91E63',
};

// Web <select> for the payout-country claim in the Connect Bank modal
// (same raw-select pattern as AdminExpensesScreen).
const PAYOUT_COUNTRY_SELECT_STYLE = {
  width: '100%',
  backgroundColor: '#0a0a0a',
  color: '#ffffff',
  border: '1px solid #333',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 14,
  outline: 'none',
  cursor: 'pointer',
};

// ----------------------------------------------------------------------------
// CASH DEPOSITS — DORMANT (founder decision 2026-07-03)
// Deposits into the custodial wallet are disabled until cash events return:
// post-pivot, wallet money can't enter events (entries cost Crits) or buy
// Crits, so a deposit is a dead end AND holding user-deposited funds with no
// product function is exactly the custody posture the financial pivot was
// built to avoid. The wallet is now an EARNINGS wallet: money in only from
// CritCash (creator comp, refunds), out via withdrawal.
// The Deposit tab + modal code below is KEPT INTACT — flip this to true
// (and re-enable the Deposits feature toggle in Payment Settings) when cash
// games come back.
// ----------------------------------------------------------------------------
const CASH_DEPOSITS_ENABLED = false;
// 2026-07-09 (founder): no custody — creator comp is paid out automatically
// when it becomes available, so there is nothing to "withdraw". Hides the
// withdraw tab/form; the screen shows earnings balance + metrics + history.
// The withdrawal machinery is kept dormant behind this flag.
const WITHDRAWALS_ENABLED = false;

const CRIT_DIE = require('../../assets/20_gdice.webp');

const QUICK_DEPOSIT_AMOUNTS = [10, 25, 50, 100];
const TURBO_FLAT_FEE_CENTS = 1000; // $10
const TURBO_FLAT_THRESHOLD_CENTS = 50000; // $500
const TURBO_PERCENT_FEE = 2; // 2%

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatCurrency = (cents) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format((cents || 0) / 100);
};

const calculateTurboFee = (amountCents) => {
  if (!amountCents || amountCents <= 0) return 0;
  if (amountCents <= TURBO_FLAT_THRESHOLD_CENTS) {
    return TURBO_FLAT_FEE_CENTS;
  }
  return Math.ceil(amountCents * (TURBO_PERCENT_FEE / 100));
};

// Render destination as user-facing string for the summary / confirm modal.
// Examples: "Bank ending in 6782", "Visa debit card ending in 4242". Falls
// back to a generic label when we don't have details yet (rare — only when
// no bank is connected yet, in which case the eligibility warning fires first).
const formatPayoutDestination = (dest) => {
  if (!dest) return 'connected account';
  const kind = dest.type === 'card' ? 'card' : 'bank account';
  const brand = dest.brand ? `${dest.brand} ` : '';
  const tail = dest.last4 ? `ending in ${dest.last4}` : '';
  return `${brand}${kind}${tail ? ' ' + tail : ''}`.trim();
};

const formatTransactionLabel = (reason, type, balanceType) => {
  const labels = {
    'stripe_deposit': 'Deposit',
    'deposit': 'Deposit',
    // Legacy cash-event rows (nothing writes these reasons to wallet_ledger
    // today); labels stay true for old rows.
    'event_payout': 'Event Credit (Legacy)',
    'event_winnings': 'Event Credit (Legacy)',
    'winner_payout': 'Event Credit (Legacy)',
    'refund': 'Entry Refund',
    'event_refund': 'Entry Refund',
    'entry_refund': 'Entry Refund',
    'event_entry_refund': 'Entry Refund',
    'event_canceled': 'Entry Refund',
    'withdrawal_failed': 'Withdrawal Refund',
    'withdrawal_canceled': 'Withdrawal Refund',
    // Cash host rewards are legacy (host rewards write the Crit ledger, never
    // wallet_ledger); the label stays true for old rows.
    'host_reward': 'Host Reward (Legacy)',
    // Monthly-run credit rows (past rows included). Neutral label, matching
    // the Terms' "creator compensation" (Super Crits and Rumbles only).
    'creator_comp': 'Creator Compensation',
    // earnings.js CREDIT_REASON: tip_earning = a matured Super Crit / squad
    // Super Crit / Rumble (stream, squad or event) share; membership_earning =
    // a membership share.
    'tip_earning': 'Super Crit / Rumble Share',
    'membership_earning': 'Membership Share',
    'auto_payout': 'Payout to Bank',
    'auto_payout_failed': 'Payout Returned',
    'payout_failed': 'Payout Returned',
    'payout_canceled': 'Payout Returned',
    'bonus': 'Bonus',
    'gift_card_redemption': 'Gift Card',
    'gift_card_received': 'Gift Card',
    'entry_fee': 'Entry (Legacy)',
    'event_entry': 'Entry (Legacy)',
    'withdrawal_request': 'Withdrawal',
    'withdrawal': 'Withdrawal',
    'gift_card_purchase': 'Gift Card Purchase',
    'merch_purchase': 'Merch Purchase',
    'merch_refund': 'Merch Refund',
  };
  
  let label = labels[reason];
  if (!label) {
    const r = (reason || '').toLowerCase();
    if (r.includes('gift')) label = 'Gift Card';
    else if (r.includes('deposit')) label = 'Deposit';
    else if (r.includes('refund')) label = 'Refund';
    else if (r.includes('winning')) label = 'Event Credit (Legacy)';
    else if (r.includes('payout')) label = 'Payout';
    else if (r.includes('entry') || r.includes('fee')) label = 'Entry (Legacy)';
    else if (r.includes('withdraw')) label = 'Withdrawal';
    else label = reason?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || (type === 'credit' ? 'Credit' : 'Debit');
  }
  
  if (balanceType === 'gift') label += ' (Gift)';
  return label;
};

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function WalletScreen({ navigation, route }) {
  const { showError } = useError();
  const isFocused = useIsFocused();
  const functions = getFunctions();
  const critsEnabled = useCritsEnabled(); // settings/economy.critsEnabled
  // Memberships (3.5a channel, 3.5b team): once either kind has ever
  // launched, the compensation source lists name them too (a paused launch
  // never drops them; a team-only launch counts). The team settle sentence
  // keys on the team flag ALONE.
  const { teamEverLaunched, anyEverLaunched } = useMembershipsLive();

  // Balance state
  const [balanceCents, setBalanceCents] = useState(0);
  const [giftBalanceCents, setGiftBalanceCents] = useState(0);
  const [heldBalanceCents, setHeldBalanceCents] = useState(0);
  // Direct earnings settling (evergreen 3.1): tips credit pending at the
  // charge and join balanceCents 48h later. Never part of totalAvailable.
  const [pendingEarningsCents, setPendingEarningsCents] = useState(0);
  const [critBalance, setCritBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // Tab state
  const [activeTab, setActiveTab] = useState(CASH_DEPOSITS_ENABLED ? 'deposit' : 'withdraw'); // 'deposit' | 'withdraw'

  // Deposit state
  const [depositAmount, setDepositAmount] = useState('');
  const [depositSubmitting, setDepositSubmitting] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);

  // Withdraw state
  const [withdrawMethod, setWithdrawMethod] = useState('standard');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);

  // IRS 1099-MISC tax status — drives the year-end-gate banner + threshold chip
  const [taxStatus, setTaxStatus] = useState(null);
  // Defensive: if admin toggles Turbo Pay off mid-session and the user had
  // 'turbo' selected, snap back to 'standard' so the UI matches reality.
  useEffect(() => {
    if (eligibility && eligibility.turboPayEnabled === false && withdrawMethod === 'turbo') {
      setWithdrawMethod('standard');
    }
  }, [eligibility?.turboPayEnabled, withdrawMethod]);
  const [showBankModal, setShowBankModal] = useState(false);
  // Payout country is the member's own claim (founder 2026-10-14: location
  // tracker removed). Sent to createConnectAccount; the server uses it only
  // when it creates a new payout account.
  const [payoutCountry, setPayoutCountry] = useState('US');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  // Withdrawable balance breakdown — populated by getWithdrawableBalance.
  // Differs from wallet balance: deposits in Stripe's pending balance (~2 days
  // to clear for cards) are credited to the user wallet immediately but can't
  // be transferred out yet.
  const [withdrawable, setWithdrawable] = useState({
    availableCents: 0,
    pendingDepositsCents: 0,
    pendingDeposits: [],
    loaded: false,
  });

  // Transaction state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Merch order state — surfaced alongside wallet transactions in Recent
  // Activity so buyers can find their purchase history from the wallet too.
  // Click handler routes to MerchOrderHistory.
  const [merchOrders, setMerchOrders] = useState([]);

  // Creator metrics — this month's audience totals (plain stats), from
  // getMyCreatorCompEarnings. null = not loaded (section hidden until the
  // first successful fetch). The per-axis signals and the projected amount it
  // also returns are deliberately not rendered (copy doctrine v3: no stat
  // shown as a money driver, no activity-based pay estimate).
  const [compMetrics, setCompMetrics] = useState(null);

  // Payout account (monetary-account view, 2026-07-09). Comp is PUSHED to the
  // creator's bank automatically — these drive the Payout Account section that
  // replaced the withdraw tab: Connect onboarding status + automatic payout
  // history from the daily sweep.
  const [connectStatus, setConnectStatus] = useState(null);
  const [payoutInfo, setPayoutInfo] = useState(null);

  // Shares in a longer review, and withheld shares (purchase limits, 3.7):
  // {heldCents, heldUntilMs, withheldCents} from getMyEarningsReview. null =
  // not loaded or the read failed; both lines stay hidden until an amount
  // is above zero.
  const [earningsReview, setEarningsReview] = useState(null);

  // Computed values
  const totalAvailable = balanceCents + giftBalanceCents;
  // Standard cap = wallet balance minus settling deposits (cleared funds only).
  // Falls back to full balance until the first getWithdrawableBalance call
  // resolves (avoids showing $0 mid-load).
  const standardWithdrawableBalance = withdrawable.loaded ? withdrawable.availableCents : balanceCents;
  // Turbo cap is the binding ceiling Stripe's available balance allows (the
  // backend computes this as floor(stripeAvailable/1.015) - $0.50 to absorb
  // the 1.5% instant-payout fee). Falls back to the standard cap when
  // eligibility hasn't loaded yet OR the Stripe balance lookup failed (the
  // backend returns null stripeAvailableCents in that case).
  const turboWithdrawableBalance = (eligibility?.limits?.turbo?.max != null)
    ? Math.min(standardWithdrawableBalance, eligibility.limits.turbo.max)
    : standardWithdrawableBalance;
  // Method-aware ceiling — the headline number, MAX button, and validation
  // all key off this so the user sees what they can ACTUALLY withdraw via
  // their selected method (catches the test-mode-deposit-in-pending case
  // where Stripe available < wallet cleared, BEFORE the confirm-modal flow).
  const withdrawableBalance = withdrawMethod === 'turbo'
    ? turboWithdrawableBalance
    : standardWithdrawableBalance;
  // Wallet lines for shares in review (STEP3_5A_TIERS_DESIGN 5.14). Never the
  // line, the reason or the payer; null hides a line.
  const reviewHeldCents = Math.max(0, Number(earningsReview?.heldCents) || 0);
  const reviewHeldUntilMs = Number(earningsReview?.heldUntilMs);
  const reviewHeldLine = reviewHeldCents > 0 && Number.isFinite(reviewHeldUntilMs) && reviewHeldUntilMs > 0
    ? `${formatCurrency(reviewHeldCents)} of this is in a longer review until ${new Date(reviewHeldUntilMs).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`
    : null;
  const reviewWithheldCents = Math.max(0, Number(earningsReview?.withheldCents) || 0);
  const reviewWithheldLine = reviewWithheldCents > 0
    ? `${formatCurrency(reviewWithheldCents)} is withheld after a review of a purchase (Terms Section 8). Questions? Contact support@critcash.com.`
    : null;
  const withdrawAmountCents = Math.round((parseFloat(withdrawAmount) || 0) * 100);
  const withdrawFeeCents = withdrawMethod === 'turbo' ? calculateTurboFee(withdrawAmountCents) : 0;
  const withdrawTotalDeduction = withdrawAmountCents + withdrawFeeCents;

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  // Subscribe to user's balances
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user?.uid) {
      setBalanceCents(0);
      setGiftBalanceCents(0);
      setHeldBalanceCents(0);
      setBalanceLoading(false);
      return;
    }

    setBalanceLoading(true);
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (snap) => {
      const data = snap.data() || {};
      setBalanceCents(Number(data.balanceCents || 0));
      setGiftBalanceCents(Number(data.giftBalanceCents || 0));
      setHeldBalanceCents(Number(data.heldBalanceCents || 0));
      setPendingEarningsCents(Math.max(0, Number(data.pendingEarningsCents || 0)));
      setCritBalance(Math.max(0, Math.floor(Number(data.critBalance || 0))));
      setBalanceLoading(false);
    }, (error) => {
      console.error('Error fetching balance:', error);
      setBalanceLoading(false);
    });

    return unsubscribe;
  }, []);

  // Fetch withdrawal eligibility
  const fetchEligibility = useCallback(async () => {
    try {
      setEligibilityLoading(true);
      const getEligibility = httpsCallable(functions, 'getWithdrawalEligibility');
      const result = await getEligibility();
      setEligibility(result.data);
    } catch (err) {
      console.error('Error fetching eligibility:', err);
      setEligibility({
        eligible: false,
        issues: ['Unable to verify eligibility'],
        bankConnected: false,
        limits: {
          standard: { min: 1000, max: 1000000, dailyRemaining: 1000000 },
          turbo: { min: 500, max: 500000, dailyRemaining: 500000 },
        },
      });
    } finally {
      setEligibilityLoading(false);
    }
  }, [functions]);

  // Fetch withdrawable balance breakdown (cleared funds vs pending deposits)
  const fetchWithdrawable = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'getWithdrawableBalance');
      const result = await fn();
      const data = result.data || {};
      setWithdrawable({
        availableCents: data.availableCents || 0,
        pendingDepositsCents: data.pendingDepositsCents || 0,
        pendingDeposits: data.pendingDeposits || [],
        loaded: true,
      });
    } catch (err) {
      console.error('Error fetching withdrawable balance:', err);
      // Leave loaded:false so withdrawableBalance falls back to full balance
    }
  }, [functions]);

  // Fetch tax status — drives the year-end gate banner + threshold reminder chip
  const fetchTaxStatus = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'getMyTaxStatus');
      const result = await fn();
      setTaxStatus(result.data || null);
    } catch (err) {
      // Non-fatal — if tax status fetch fails, the banner just doesn't render
      console.warn('[WalletScreen] tax status fetch failed:', err.message);
    }
  }, [functions]);

  // Fetch transactions + merch orders together. Both feed the Recent
  // Activity panel; we render them as a single merged list sorted by date.
  // Failures are non-fatal — if merch fetch dies the wallet feed still shows.
  const fetchTransactions = useCallback(async () => {
    try {
      setTransactionsLoading(true);
      const getTransactions = httpsCallable(functions, 'getWalletTransactions');
      const getMerchHistory = httpsCallable(functions, 'getMerchOrderHistory');
      const [txResult, merchResult] = await Promise.all([
        getTransactions({ limit: 15 }),
        getMerchHistory({ limit: 15 }).catch((err) => {
          console.warn('Merch order history fetch failed (non-fatal):', err?.message);
          return { data: { orders: [] } };
        }),
      ]);
      setTransactions(txResult.data.transactions || []);
      setMerchOrders(merchResult.data?.orders || []);
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setTransactionsLoading(false);
      setRefreshing(false);
    }
  }, [functions]);

  // Fetch creator metrics (this month's audience totals).
  // Non-fatal — the section just stays hidden if it fails.
  const fetchCompMetrics = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'getMyCreatorCompEarnings');
      const result = await fn();
      setCompMetrics(result.data || null);
    } catch (err) {
      console.warn('[WalletScreen] comp metrics fetch failed:', err.message);
    }
  }, [functions]);

  // Payout-account status: is the Connect account set up and payable? Drives
  // the "Set up payouts" vs "Payouts on · Bank ····1234" state.
  const fetchConnectStatus = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'getConnectAccountStatus');
      const result = await fn();
      setConnectStatus(result.data || null);
    } catch (err) {
      console.warn('[WalletScreen] connect status fetch failed:', err.message);
    }
  }, [functions]);

  // Automatic payout history (compPayouts rows for this user).
  const fetchPayoutHistory = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'getMyPayoutHistory');
      const result = await fn();
      setPayoutInfo(result.data || null);
    } catch (err) {
      console.warn('[WalletScreen] payout history fetch failed:', err.message);
    }
  }, [functions]);

  // Held and withheld shares (getMyEarningsReview). Non-fatal, like the
  // reads above: a failure keeps both lines hidden.
  const fetchEarningsReview = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'getMyEarningsReview');
      const result = await fn();
      setEarningsReview(result.data || null);
    } catch (err) {
      console.warn('[WalletScreen] earnings review fetch failed:', err.message);
      setEarningsReview(null);
    }
  }, [functions]);

  useEffect(() => {
    if (isFocused) {
      fetchTransactions();
      fetchEligibility();
      fetchWithdrawable();
      fetchTaxStatus();
      fetchCompMetrics();
      fetchConnectStatus();
      fetchPayoutHistory();
      fetchEarningsReview();
    }
  }, [isFocused, fetchTransactions, fetchEligibility, fetchWithdrawable, fetchTaxStatus, fetchCompMetrics, fetchConnectStatus, fetchPayoutHistory, fetchEarningsReview]);

  // Check for return params
  useEffect(() => {
    const params = route?.params;
    if (params?.connect_success) {
      showError(
        'Bank account connected successfully!',
        'Your earnings will now be sent to this account automatically.'
      );
      fetchEligibility();
      fetchConnectStatus();
    } else if (params?.connect_refresh) {
      showError(
        'Please complete your bank connection',
        'Stripe needs a few more details before payouts can be enabled.'
      );
    } else if (params?.deposit_success) {
      showError(
        'Deposit successful!',
        'Funds are available in your wallet now.'
      );
    }
  }, [route?.params]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchTransactions();
    fetchEligibility();
    fetchCompMetrics();
    fetchConnectStatus();
    fetchPayoutHistory();
  };

  // ============================================================================
  // DEPOSIT HANDLERS
  // ============================================================================

  const handleDepositPress = async () => {
    const amountCents = Math.round((parseFloat(depositAmount) || 0) * 100);
    if (amountCents < 500) {
      showError('Minimum deposit is $5');
      return;
    }
    if (amountCents > 100000) {
      showError('Maximum deposit is $1,000');
      return;
    }

    setShowDepositModal(true);
  };

  const confirmDeposit = async () => {
    setShowDepositModal(false);
    const amountCents = Math.round((parseFloat(depositAmount) || 0) * 100);

    try {
      setDepositSubmitting(true);
      const createCheckout = httpsCallable(functions, 'createDepositCheckout');
      const result = await createCheckout({ amountCents });

      if (result.data.url) {
        if (typeof window !== 'undefined') {
          window.location.href = result.data.url;
        }
      }
    } catch (err) {
      console.error('Deposit error:', err);
      // Raw Stripe / cloud-function errors are scary for a first-time
      // depositor ("Invalid API key", "card_declined", "internal") and
      // shed conversion. Detect common patterns and surface friendly,
      // actionable copy. Falls through to a generic-but-warm message
      // for anything we don't recognize. Added 2026-05-30 pre-launch UX
      // audit — first-deposit clarity is the single biggest funnel risk
      // in the controlled rollout.
      const raw = (err?.message || '').toLowerCase();
      let friendly;
      if (raw.includes('card_declined') || raw.includes('declined')) {
        friendly = 'Your card was declined. Try a different card, or contact your bank if it keeps happening.';
      } else if (raw.includes('insufficient_funds')) {
        friendly = 'Insufficient funds on the card. Try a different payment method.';
      } else if (raw.includes('expired_card')) {
        friendly = 'That card has expired. Try a different one.';
      } else if (raw.includes('unauthenticated') || raw.includes('sign in')) {
        friendly = 'You need to be signed in to deposit. Sign in and try again.';
      } else if (raw.includes('failed-precondition') || raw.includes('disabled')) {
        friendly = 'Deposits are temporarily paused. Try again in a few minutes or check our Discord for updates.';
      } else if (raw.includes('network') || raw.includes('timeout') || raw.includes('fetch')) {
        friendly = "We couldn't reach our payment processor. Check your internet and try again.";
      } else {
        friendly = "We couldn't start your deposit. Try again in a moment, or email support@critcash.com if it keeps failing.";
      }
      showError('Deposit failed', friendly);
    } finally {
      setDepositSubmitting(false);
    }
  };

  // ============================================================================
  // WITHDRAW HANDLERS
  // ============================================================================

  const handleConnectBank = () => {
    setShowBankModal(true);
  };

  const confirmBankConnection = async () => {
    setShowBankModal(false);
    try {
      setWithdrawSubmitting(true);
      const createConnect = httpsCallable(functions, 'createConnectAccount');
      // The server ignores country when a payout account already exists
      // (its country is fixed at creation), so "Finish payout setup" can
      // send it too.
      const result = await createConnect({ country: payoutCountry });

      if (result.data.url) {
        if (typeof window !== 'undefined') {
          window.location.href = result.data.url;
        }
      }
    } catch (err) {
      console.error('Error connecting bank:', err);
      // The server writes member-facing text for these two codes (bad
      // country value, or a country payouts can't be set up for yet).
      const code = String(err?.code || '').replace(/^functions\//, '');
      if ((code === 'failed-precondition' || code === 'invalid-argument') && err?.message) {
        showError(err.message);
      } else {
        showError('Unable to start bank connection');
      }
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  // Open the Stripe Express dashboard so the user can view/update the bank
  // account their automatic payouts land in.
  const handleManagePayoutAccount = async () => {
    try {
      setWithdrawSubmitting(true);
      const fn = httpsCallable(functions, 'createConnectDashboardLink');
      const result = await fn();
      if (result.data.url && typeof window !== 'undefined') {
        window.open(result.data.url, '_blank');
      }
    } catch (err) {
      console.error('Error opening payout dashboard:', err);
      showError('Unable to open your payout account right now');
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const handleWithdrawPress = () => {
    if (withdrawAmountCents < 500) {
      showError('Minimum withdrawal is $5');
      return;
    }
    if (withdrawTotalDeduction > balanceCents) {
      showError('Insufficient balance');
      return;
    }
    if (withdrawTotalDeduction > withdrawableBalance) {
      // Two cases: standard cap exceeded (funds settling) vs turbo cap
      // exceeded (Stripe available limits turbo specifically). Tailor the
      // message + the suggested next step so the user knows what to do.
      if (withdrawMethod === 'turbo' && withdrawableBalance < standardWithdrawableBalance) {
        showError(
          'Not enough cleared funds for Turbo Pay',
          `Turbo Pay needs cleared funds in Stripe and only ${formatCurrency(withdrawableBalance)} is currently available. Switch to Standard Transfer to withdraw up to ${formatCurrency(standardWithdrawableBalance)} (processes after a 24-hour hold and works with funds still clearing).`
        );
      } else {
        const pendingStr = formatCurrency(withdrawable.pendingDepositsCents);
        showError(
          'Funds still clearing',
          `${pendingStr} of your balance is still clearing in Stripe. Available to withdraw: ${formatCurrency(withdrawableBalance)}. Recent deposits take 1-2 business days to become withdrawable.`
        );
      }
      return;
    }
    setShowWithdrawModal(true);
  };

  const confirmWithdraw = async () => {
    setShowWithdrawModal(false);

    try {
      setWithdrawSubmitting(true);
      const createWithdrawal = httpsCallable(functions, 'createWithdrawal');
      const result = await createWithdrawal({
        method: withdrawMethod,
        amountCents: withdrawAmountCents,
      });

      if (result.data.success) {
        const successBody =
          result.data.message ||
          (withdrawMethod === 'turbo'
            ? 'Funds typically land within minutes via Turbo Pay.'
            : 'Funds will arrive in 1-2 business days.');
        showError(
          `Withdrawal of ${formatCurrency(withdrawAmountCents)} initiated!`,
          successBody
        );
        setWithdrawAmount('');
        fetchEligibility();
        fetchTransactions();
        fetchWithdrawable();
      }
    } catch (err) {
      console.error('Withdrawal error:', err);
      showError(err.message || 'Withdrawal failed');
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const handleMaxWithdraw = () => {
    // Use withdrawable (cleared) funds — not full balance — so users can't try
    // to withdraw deposits that are still pending in Stripe's available rollover.
    const cap = withdrawableBalance;
    const maxAmount = withdrawMethod === 'turbo'
      ? Math.max(0, cap - TURBO_FLAT_FEE_CENTS)
      : cap;
    if (maxAmount > 0) {
      setWithdrawAmount((maxAmount / 100).toFixed(2));
    }
  };

  // ============================================================================
  // RENDER: TAX-COMPLIANCE BANNER (IRS 1099-MISC)
  // Shows a yellow gate banner if the user is currently blocked from withdrawals
  // due to a prior-year obligation, or a softer chip when they've crossed a
  // threshold this year without submitting a tax form yet.
  // ============================================================================

  const renderTaxBanner = () => {
    if (!taxStatus) return null;

    // Hard-block banner: prior year crossed $600 without doc
    if (taxStatus.withdrawalsGated) {
      return (
        <TouchableOpacity
          style={styles.taxGateBanner}
          onPress={() => navigation.navigate('TaxInfo')}
          activeOpacity={0.85}
        >
          <Ionicons name="lock-closed" size={18} color="#ef4444" />
          <View style={{ flex: 1, marginHorizontal: 10 }}>
            <Text style={styles.taxGateBannerTitle}>Tax form required</Text>
            <Text style={styles.taxGateBannerBody} numberOfLines={2}>
              {taxStatus.gateReason || 'Submit your W-9 or W-8BEN so your automatic payouts can resume.'}
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={18} color="#ef4444" />
        </TouchableOpacity>
      );
    }

    // Soft chip: crossed a threshold this year but no doc yet
    const ytdCents = taxStatus.currentYear?.totalPrizeCents || 0;
    const threshold = taxStatus.thresholdCents || 60000;
    const hasDoc = !!taxStatus.activeTaxDocument;
    if (!hasDoc && ytdCents >= (taxStatus.earlyWarningCents || 50000)) {
      const overThreshold = ytdCents >= threshold;
      return (
        <TouchableOpacity
          style={overThreshold ? styles.taxWarnBanner : styles.taxInfoBanner}
          onPress={() => navigation.navigate('TaxInfo')}
          activeOpacity={0.85}
        >
          <Ionicons
            name={overThreshold ? 'warning' : 'information-circle'}
            size={16}
            color={overThreshold ? '#f59e0b' : '#3b82f6'}
          />
          <Text
            style={overThreshold ? styles.taxWarnBannerText : styles.taxInfoBannerText}
            numberOfLines={2}
          >
            {overThreshold
              ? `You've earned $${(ytdCents/100).toFixed(0)}+ from CritCash this year. Submit your tax form so payouts aren't paused in January.`
              : `You've earned $${(ytdCents/100).toFixed(0)}+ from CritCash this year. A tax form will be required if you reach $600.`}
          </Text>
          <Ionicons name="chevron-forward" size={14} color={overThreshold ? '#f59e0b' : '#3b82f6'} />
        </TouchableOpacity>
      );
    }

    return null;
  };

  // ============================================================================
  // RENDER: BALANCE SECTION
  // ============================================================================

  const renderBalance = () => (
    <View style={styles.balanceSection}>
      {/* Crits Card — the spend currency. Earnings (below) is the take-home.
          Hidden when the Crit economy flag is off. */}
      {critsEnabled && (
        <View style={styles.critCard}>
          <Image source={CRIT_DIE} style={styles.critCardDie} resizeMode="contain" />
          <View style={styles.critCardBody}>
            <Text style={styles.critCardLabel}>Crits</Text>
            <Text style={styles.critCardValue}>
              {balanceLoading ? '—' : critBalance.toLocaleString('en-US')}
            </Text>
            <Text style={styles.critCardHint}>Enter events and climb the ladder.</Text>
          </View>
          <TouchableOpacity
            style={styles.getCritsBtn}
            onPress={() => navigation.navigate('Subscription')}
          >
            <Text style={styles.getCritsBtnText}>Get Crits</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Main Earnings Card */}
      <View style={styles.balanceCard}>
        <Text style={styles.balanceLabel}>Earnings</Text>
        <Text style={styles.balanceAmount}>
          {balanceLoading ? (
            <ActivityIndicator color={COLORS.GOLD} size="small" />
          ) : (
            formatCurrency(totalAvailable)
          )}
        </Text>

        {/* Balance Breakdown */}
        <View style={styles.balanceBreakdown}>
          <View style={styles.breakdownItem}>
            <View style={[styles.breakdownDot, { backgroundColor: COLORS.GREEN }]} />
            <Text style={styles.breakdownLabel}>{pendingEarningsCents > 0 ? 'Available' : 'Wallet'}</Text>
            <Text style={[styles.breakdownValue, { color: COLORS.GREEN }]}>
              {formatCurrency(balanceCents)}
            </Text>
          </View>

          {pendingEarningsCents > 0 && (
            <View style={styles.breakdownItem}>
              <View style={[styles.breakdownDot, { backgroundColor: COLORS.BLUE }]} />
              <Text style={styles.breakdownLabel}>Pending</Text>
              <Text style={[styles.breakdownValue, { color: COLORS.BLUE }]}>
                {formatCurrency(pendingEarningsCents)}
              </Text>
            </View>
          )}

          {giftBalanceCents > 0 && (
            <View style={styles.breakdownItem}>
              <View style={[styles.breakdownDot, { backgroundColor: COLORS.GIFT }]} />
              <Text style={styles.breakdownLabel}>Gift</Text>
              <Text style={[styles.breakdownValue, { color: COLORS.GIFT }]}>
                {formatCurrency(giftBalanceCents)}
              </Text>
            </View>
          )}

          {heldBalanceCents > 0 && (
            <View style={styles.breakdownItem}>
              <View style={[styles.breakdownDot, { backgroundColor: COLORS.DANGER }]} />
              <Text style={styles.breakdownLabel}>Held</Text>
              <Text style={[styles.breakdownValue, { color: COLORS.DANGER }]}>
                {formatCurrency(heldBalanceCents)}
              </Text>
            </View>
          )}
        </View>

        {/* Only the Wallet portion pays out — without this line, a gift-credit
            holder sees an Earnings headline bigger than their queued payout
            with nothing on screen reconciling the two. */}
        {giftBalanceCents > 0 && (
          <Text style={styles.balanceGiftNote}>
            Gift credit is spendable on CritCash but isn't included in bank payouts.
          </Text>
        )}
      </View>

      {/* Gift Cards Button — gift cards are Crit-denominated, so it follows the flag. */}
      {critsEnabled && (
        <TouchableOpacity
          style={styles.giftCardButton}
          onPress={() => navigation.navigate('GiftCard')}
        >
          <MaterialCommunityIcons name="gift" size={24} color="#E91E63" style={styles.giftCardIcon} />
          <View style={styles.giftCardContent}>
            <Text style={styles.giftCardTitle}>Gift Cards</Text>
            <Text style={styles.giftCardSubtitle}>Buy or redeem</Text>
          </View>
          <Feather name="chevron-right" size={24} color="#888" />
        </TouchableOpacity>
      )}
    </View>
  );

  // ============================================================================
  // RENDER: CREATOR METRICS — this month's audience as plain stats (hours
  // watched / signed-in viewers / events), the sign-in nudge for signed-out
  // visits, and the link to the full dashboard. The audience here is this
  // month's event matches only, and the signed-out count is one per browser
  // tab session (EventWatchScreen), not per person, so the hint says
  // "signed-out visits to your event matches" (AM-2). Sits right under
  // Earnings, so it shows NO per-axis activity chips and NO pay estimate:
  // nothing here may read as a money driver (copy doctrine v3; money comes
  // only from Super Crits, Rumbles and channel and team memberships).
  // ============================================================================

  const renderCreatorMetrics = () => {
    if (!compMetrics?.thisMonth) return null;
    const m = compMetrics.thisMonth;
    const aud = compMetrics.audience || {};

    const [yy, mm] = (m.monthKey || '').split('-').map(Number);
    const monthName = yy && mm
      ? new Date(Date.UTC(yy, mm - 1, 1)).toLocaleString('en-US', { month: 'long', timeZone: 'UTC' })
      : 'This month';
    const hoursWatched = Math.round(((aud.watchMinutes || 0) / 60) * 10) / 10;

    return (
      <View style={styles.metricsSection}>
        <View style={styles.metricsHeader}>
          <Text style={styles.sectionTitle}>Your Creator Metrics</Text>
          <Text style={styles.metricsMonth}>{monthName}</Text>
        </View>

        {(aud.viewers > 0 || aud.anonViewers > 0) && (
          <View style={styles.metricsAudienceRow}>
            <View style={styles.metricsAudienceCell}>
              <Text style={styles.metricsAudienceValue}>{hoursWatched}</Text>
              <Text style={styles.metricsAudienceLabel}>hours watched</Text>
            </View>
            <View style={styles.metricsAudienceCell}>
              <Text style={styles.metricsAudienceValue}>{(aud.viewers || 0).toLocaleString('en-US')}</Text>
              <Text style={styles.metricsAudienceLabel}>signed-in viewers</Text>
            </View>
            <View style={styles.metricsAudienceCell}>
              <Text style={styles.metricsAudienceValue}>{(aud.events || 0).toLocaleString('en-US')}</Text>
              <Text style={styles.metricsAudienceLabel}>events</Text>
            </View>
          </View>
        )}

        {aud.anonViewers > 0 && (
          <View style={styles.metricsConversionBox}>
            <Ionicons name="eye-outline" size={16} color={COLORS.GOLD} style={{ marginRight: 8 }} />
            <Text style={styles.metricsConversionText}>
              <Text style={styles.metricsConversionCount}>
                {`${aud.anonViewers.toLocaleString('en-US')} signed-out visit${aud.anonViewers === 1 ? '' : 's'}`}
              </Text>
              {` to your event matches this month. Signed-in viewers can chat, follow you, and support you with ${anyEverLaunched ? 'Super Crits, Rumbles and memberships' : 'Super Crits and Rumbles'}, so invite your audience to sign up.`}
            </Text>
          </View>
        )}

        {/* Footer CTA → the full dashboard: streaming time, events, games,
            audience, rumbles, Super Crits (founder 2026-07-10) */}
        <TouchableOpacity
          style={styles.metricsFullLink}
          onPress={() => navigation.navigate('CreatorMetrics')}
        >
          <MaterialCommunityIcons name="chart-box-outline" size={18} color={COLORS.GOLD} style={{ marginRight: 8 }} />
          <Text style={styles.metricsFullLinkText}>View your full Creator Metrics</Text>
          <Feather name="chevron-right" size={18} color="#888" />
        </TouchableOpacity>
      </View>
    );
  };

  // ============================================================================
  // RENDER: PAYOUT ACCOUNT — the monetary-account view (no custody, 2026-07-09).
  // Earnings are pushed to the creator's bank automatically: this section shows
  // payout setup state (Stripe Connect), the daily sweep, the pending amount,
  // and automatic payout history. It replaced the withdraw tab.
  // ============================================================================

  // autoPayoutSweep (functions/modules/compPayouts.js) runs every day at
  // 04:00 UTC and sweeps any payable balance, so the card states the daily
  // cadence rather than a monthly date.
  const renderPayoutAccount = () => {
    const connected = !!connectStatus?.connected;
    const payoutsOn = connected && !!connectStatus?.payoutsEnabled;
    const bankTail = connectStatus?.bankLast4 ? `Bank ····${connectStatus.bankLast4}` : 'Bank connected';
    const payouts = payoutInfo?.payouts || [];
    const taxGated = !!taxStatus?.withdrawalsGated;
    // Identity (KYC) gates the payout sweep even with the bank fully set up.
    // Only prompt on an explicit false — while connectStatus is still loading
    // we don't know, and nagging verified users would be worse.
    const needsIdentity = connectStatus ? connectStatus.identityVerified === false : false;
    const fullyPayable = payoutsOn && !needsIdentity;

    const payoutStatusChip = (status) => {
      const map = {
        paid: { label: 'Sent', color: COLORS.GREEN },
        processing: { label: 'Processing', color: COLORS.BLUE },
        failed: { label: 'Returned', color: COLORS.DANGER },
      };
      const s = map[status] || { label: status, color: COLORS.DIM };
      return (
        <View style={[styles.payoutStatusChip, { borderColor: s.color }]}>
          <Text style={[styles.payoutStatusChipText, { color: s.color }]}>{s.label}</Text>
        </View>
      );
    };

    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Payout Account</Text>

        {/* How it works + daily payouts */}
        <View style={styles.payoutCard}>
          <View style={styles.payoutRow}>
            <Ionicons name="flash" size={18} color={COLORS.GOLD} style={{ marginRight: 10 }} />
            <View style={{ flex: 1 }}>
              <Text style={styles.payoutRowTitle}>Payouts are automatic</Text>
              <Text style={styles.payoutRowBody}>
                Payable earnings are paid automatically to the payout account you
                connect.
              </Text>
            </View>
          </View>
          <View style={[styles.payoutRow, { marginTop: 12 }]}>
            <Ionicons name="calendar" size={18} color={COLORS.GOLD} style={{ marginRight: 10 }} />
            <View style={{ flex: 1 }}>
              <Text style={styles.payoutRowTitle}>Payouts go out daily</Text>
              {balanceCents > 0 ? (
                <Text style={styles.payoutRowBody}>
                  {formatCurrency(balanceCents)}{' '}
                  {fullyPayable
                    ? 'is queued and goes out the next morning — any balance you hold is swept daily.'
                    : needsIdentity
                      ? 'is waiting for you — verify your identity below to receive it.'
                      : 'is waiting for you — finish payout setup below to receive it.'}
                </Text>
              ) : (
                <Text style={styles.payoutRowBody}>
                  Your first payout can take up to a week to arrive while your account is new; after that, bank transfers typically land in about 2 business days.
                </Text>
              )}
            </View>
          </View>

          {pendingEarningsCents > 0 && (
            <View style={[styles.payoutRow, { marginTop: 12 }]}>
              <Ionicons name="time" size={18} color={COLORS.BLUE} style={{ marginRight: 10 }} />
              <View style={{ flex: 1 }}>
                <Text style={styles.payoutRowTitle}>Pending: {formatCurrency(pendingEarningsCents)}</Text>
                <Text style={styles.payoutRowBody}>
                  {anyEverLaunched
                    ? 'Your share of Super Crits, Rumbles and memberships'
                    : 'Your share of Super Crits and Rumbles'} is pending until it's payable, no sooner than 48 hours after the purchase, and not before CritCash has received the funds. Then it joins your next automatic payout.{teamEverLaunched ? ' Team membership shares are credited after the month they were paid in closes.' : ''}
                </Text>
                {reviewHeldLine ? <Text style={styles.payoutRowBody}>{reviewHeldLine}</Text> : null}
                {reviewWithheldLine ? <Text style={styles.payoutRowBody}>{reviewWithheldLine}</Text> : null}
              </View>
            </View>
          )}

          {/* A withheld share is no longer pending, so its line shows even
              when nothing is pending. */}
          {pendingEarningsCents <= 0 && reviewWithheldLine ? (
            <View style={[styles.payoutRow, { marginTop: 12 }]}>
              <Ionicons name="time" size={18} color={COLORS.BLUE} style={{ marginRight: 10 }} />
              <View style={{ flex: 1 }}>
                <Text style={styles.payoutRowBody}>{reviewWithheldLine}</Text>
              </View>
            </View>
          ) : null}
        </View>

        {/* Identity (KYC) step — required before any payout, independent of
            the bank connection. Once verified, the daily sweep pays the very
            next morning. */}
        {needsIdentity && (
          <View style={{ marginBottom: 10 }}>
            <TouchableOpacity
              style={styles.payoutSetupBtnAmber}
              onPress={() => navigation.navigate('Verification')}
            >
              <Ionicons name="shield-checkmark" size={18} color="#0b0b0b" style={{ marginRight: 8 }} />
              <Text style={styles.payoutSetupBtnText}>Verify your identity</Text>
            </TouchableOpacity>
            <Text style={styles.payoutSetupHint}>
              A one-time ID check is required before payouts. Verify today and
              your earnings go out the next morning.
            </Text>
          </View>
        )}

        {/* Setup state */}
        {payoutsOn ? (
          <View style={styles.payoutBankRow}>
            <Ionicons name="checkmark-circle" size={20} color={COLORS.GREEN} style={{ marginRight: 10 }} />
            <View style={{ flex: 1 }}>
              <Text style={styles.payoutBankTitle}>{bankTail}</Text>
              <Text style={styles.payoutRowBody}>Payouts on</Text>
            </View>
            <TouchableOpacity onPress={handleManagePayoutAccount} disabled={withdrawSubmitting}>
              <Text style={styles.payoutManageLink}>Manage</Text>
            </TouchableOpacity>
          </View>
        ) : connected ? (
          <TouchableOpacity style={styles.payoutSetupBtnAmber} onPress={confirmBankConnection} disabled={withdrawSubmitting}>
            <Ionicons name="alert-circle" size={18} color="#0b0b0b" style={{ marginRight: 8 }} />
            <Text style={styles.payoutSetupBtnText}>
              {withdrawSubmitting ? 'Opening…' : 'Finish payout setup'}
            </Text>
          </TouchableOpacity>
        ) : (
          <View>
            <TouchableOpacity style={styles.payoutSetupBtn} onPress={handleConnectBank} disabled={withdrawSubmitting}>
              <Ionicons name="card" size={18} color="#0b0b0b" style={{ marginRight: 8 }} />
              <Text style={styles.payoutSetupBtnText}>
                {withdrawSubmitting ? 'Opening…' : 'Set up payouts'}
              </Text>
            </TouchableOpacity>
            <Text style={styles.payoutSetupHint}>
              Add your bank once — every payout after that is automatic.
            </Text>
          </View>
        )}

        {/* Tax gate note (banner up top has the details) */}
        {taxGated && (
          <Text style={styles.payoutTaxNote}>
            Automatic payouts are paused until your tax form is on file — your
            earnings keep accruing in the meantime.
          </Text>
        )}

        {/* Payout history */}
        <Text style={styles.payoutHistoryTitle}>Payout History</Text>
        {payouts.length === 0 ? (
          <Text style={styles.emptyText}>No payouts yet</Text>
        ) : (
          payouts.map((p) => (
            <View key={p.id} style={styles.transactionItem}>
              <View style={{ flex: 1 }}>
                <Text style={styles.txType}>Payout to Bank</Text>
                <Text style={styles.txDate}>{p.createdAt ? formatDateTime(new Date(p.createdAt).toISOString()) : ''}</Text>
              </View>
              {payoutStatusChip(p.status)}
              <Text style={[styles.txAmount, styles.txAmountCredit, { marginLeft: 10 }]}>
                {formatCurrency(p.amountCents)}
              </Text>
            </View>
          ))
        )}
      </View>
    );
  };

  // ============================================================================
  // RENDER: TABS
  // ============================================================================

  // Deposit tab hidden while CASH_DEPOSITS_ENABLED is false (earnings-wallet
  // model) — the withdraw content renders alone under a single heading.
  const renderTabs = () => {
    if (!WITHDRAWALS_ENABLED) return null;
    if (!CASH_DEPOSITS_ENABLED) {
      return (
        <View style={styles.tabContainer}>
          <View style={[styles.tab, styles.tabActive]}>
          <Text style={[styles.tabText, styles.tabTextActive]}>
            Withdraw Earnings
          </Text>
          </View>
        </View>
      );
    }
    return (
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'deposit' && styles.tabActive]}
          onPress={() => setActiveTab('deposit')}
        >
          <Text style={[styles.tabText, activeTab === 'deposit' && styles.tabTextActive]}>
            Deposit
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'withdraw' && styles.tabActive]}
          onPress={() => setActiveTab('withdraw')}
        >
          <Text style={[styles.tabText, activeTab === 'withdraw' && styles.tabTextActive]}>
            Withdraw
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  // ============================================================================
  // RENDER: DEPOSIT TAB
  // ============================================================================

  const renderDepositTab = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Add Funds</Text>

      {/* Quick Amount Buttons */}
      <View style={styles.quickAmounts}>
        {QUICK_DEPOSIT_AMOUNTS.map((amount) => (
          <TouchableOpacity
            key={amount}
            style={[
              styles.quickAmountBtn,
              depositAmount === amount.toString() && styles.quickAmountBtnActive,
            ]}
            onPress={() => setDepositAmount(amount.toString())}
          >
            <Text style={[
              styles.quickAmountText,
              depositAmount === amount.toString() && styles.quickAmountTextActive,
            ]}>
              ${amount}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Custom Amount Input */}
      <View style={styles.inputRow}>
        <Text style={styles.currencySymbol}>$</Text>
        <TextInput
          style={styles.amountInput}
          value={depositAmount}
          onChangeText={setDepositAmount}
          placeholder="0"
          placeholderTextColor={COLORS.DIM}
          keyboardType="decimal-pad"
          maxLength={7}
        />
      </View>

      {/* Deposit Button */}
      <TouchableOpacity
        style={[
          styles.actionButton,
          styles.depositButton,
          (depositSubmitting || !depositAmount) && styles.actionButtonDisabled,
        ]}
        onPress={handleDepositPress}
        disabled={depositSubmitting || !depositAmount}
      >
        {depositSubmitting ? (
          <ActivityIndicator color="#000" />
        ) : (
          <Text style={styles.actionButtonText}>Continue to Deposit</Text>
        )}
      </TouchableOpacity>

      <Text style={styles.hintText}>Min $5 | Max $1,000 | Secure deposit via Stripe</Text>
    </View>
  );

  // ============================================================================
  // RENDER: WITHDRAW TAB
  // ============================================================================

  const renderWithdrawTab = () => {
    // Bank not connected
    if (!eligibility?.bankConnected) {
      return (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Withdraw Funds</Text>

          <View style={styles.withdrawableCard}>
            <Text style={styles.withdrawableLabel}>
              {withdrawMethod === 'turbo' ? 'Available for Turbo Pay' : 'Available to Withdraw'}
            </Text>
            <Text style={styles.withdrawableAmount}>{formatCurrency(withdrawableBalance)}</Text>

            {/* When Turbo's cap (Stripe-available-limited) is meaningfully
                lower than the Standard cap, hint at the alternative so the
                user knows they can withdraw more via Standard if they're
                willing to wait the 24h hold. Threshold of >$1 difference
                avoids hint-spam on tiny rounding gaps. */}
            {withdrawMethod === 'turbo'
              && turboWithdrawableBalance < standardWithdrawableBalance - 100 && (
              <Text style={styles.turboCapHint}>
                Standard Transfer allows {formatCurrency(standardWithdrawableBalance)} (funds still clearing in Stripe).
              </Text>
            )}

            {/* Funds-settling breakdown — clarifies why "Available to Withdraw"
                might be lower than the wallet's total balance. Each pending
                deposit shown with its specific clear date when known, so the
                user can plan around it. Per-deposit detail caps at 3 to
                keep the card compact; remaining count is rolled into a
                "+N more" footer. */}
            {withdrawable.loaded && withdrawable.pendingDepositsCents > 0 && (
              <View style={styles.settlingBlock}>
                <View style={styles.settlingHeaderRow}>
                  <Text style={styles.settlingHeader}>Funds Settling</Text>
                  <Text style={styles.settlingTotal}>
                    {formatCurrency(withdrawable.pendingDepositsCents)}
                  </Text>
                </View>
                {(withdrawable.pendingDeposits || []).slice(0, 3).map((d) => {
                  const clearDate = d.availableAt
                    ? new Date(d.availableAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : 'soon';
                  return (
                    <View key={d.id} style={styles.settlingRow}>
                      <Text style={styles.settlingRowAmt}>
                        {formatCurrency(d.amountCents)}
                      </Text>
                      <Text style={styles.settlingRowMeta}>
                        clears {clearDate}
                      </Text>
                    </View>
                  );
                })}
                {(withdrawable.pendingDeposits || []).length > 3 && (
                  <Text style={styles.settlingMore}>
                    + {withdrawable.pendingDeposits.length - 3} more deposit{withdrawable.pendingDeposits.length - 3 === 1 ? '' : 's'} settling
                  </Text>
                )}
                <Text style={styles.settlingNote}>
                  Settling funds can still be used to enter events — only external withdrawals wait for clearing.
                </Text>
              </View>
            )}

            {withdrawable.loaded && balanceCents !== withdrawable.availableCents && withdrawable.pendingDepositsCents === 0 && (
              <Text style={styles.giftNote}>
                Total balance: {formatCurrency(balanceCents)}
              </Text>
            )}
            {giftBalanceCents > 0 && (
              <Text style={styles.giftNote}>
                Gift balance ({formatCurrency(giftBalanceCents)}) cannot be withdrawn
              </Text>
            )}
          </View>

          <TouchableOpacity
            style={styles.connectBankCard}
            onPress={handleConnectBank}
            disabled={withdrawSubmitting}
          >
            {withdrawSubmitting ? (
              <ActivityIndicator color={COLORS.GOLD} />
            ) : (
              <>
                <MaterialCommunityIcons name="bank" size={24} color="#FCD000" style={styles.connectBankIcon} />
                <View style={styles.connectBankContent}>
                  <Text style={styles.connectBankTitle}>Connect Your Bank</Text>
                  <Text style={styles.connectBankDesc}>
                    Link a bank account to withdraw funds
                  </Text>
                </View>
                <Feather name="chevron-right" size={24} color="#888" />
              </>
            )}
          </TouchableOpacity>
        </View>
      );
    }

    // Eligibility issues
    if (eligibility?.issues?.length > 0) {
      return (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Withdraw Funds</Text>

          <View style={styles.warningCard}>
            <View style={{flexDirection: "row", alignItems: "center", marginBottom: 12}}><Ionicons name="warning" size={20} color="#FCD000" style={{marginRight: 8}} /><Text style={[styles.warningTitle, {marginBottom: 0}]}>Verification Required</Text></View>
            {eligibility.issues.map((issue, i) => (
              <Text key={i} style={styles.warningItem}>- {issue}</Text>
            ))}
            <TouchableOpacity
              style={styles.verifyButton}
              onPress={() => navigation.navigate('Verification')}
            >
              <Text style={styles.verifyButtonText}>Complete Verification</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    // Full withdrawal interface
    return (
      <View style={styles.section}>
        {/* Withdrawable Balance */}
        <View style={styles.withdrawableCard}>
          <Text style={styles.withdrawableLabel}>Withdrawable Balance</Text>
          <Text style={styles.withdrawableAmount}>{formatCurrency(withdrawableBalance)}</Text>
          {giftBalanceCents > 0 && (
            <Text style={styles.giftNote}>
              Gift balance ({formatCurrency(giftBalanceCents)}) cannot be withdrawn
            </Text>
          )}
        </View>

        {/* Funds Settling — surface pending Stripe deposits with their
            clear dates. Without this, a user who's deposited but hasn't
            cleared Stripe's settlement window sees "$0 Withdrawable" with
            no explanation. The data comes from getWithdrawableBalance,
            which pulls each deposit's balance_transaction.available_on
            timestamp from Stripe (captured at deposit time in
            stripeDeposits.js). */}
        {(withdrawable.pendingDeposits || []).length > 0 && (
          <View style={styles.settlingCard}>
            <View style={styles.settlingHeaderRow}>
              <Text style={styles.settlingHeader}>Funds Settling</Text>
              <Text style={styles.settlingTotal}>
                {formatCurrency(withdrawable.pendingDepositsCents)}
              </Text>
            </View>
            {(withdrawable.pendingDeposits || []).slice(0, 3).map((d) => {
              const clearDate = d.availableAt
                ? new Date(d.availableAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : 'soon';
              return (
                <View key={d.id} style={styles.settlingRow}>
                  <Text style={styles.settlingRowAmt}>
                    {formatCurrency(d.amountCents)}
                  </Text>
                  <Text style={styles.settlingRowMeta}>
                    clears {clearDate}
                  </Text>
                </View>
              );
            })}
            {(withdrawable.pendingDeposits || []).length > 3 && (
              <Text style={styles.settlingMore}>
                + {withdrawable.pendingDeposits.length - 3} more deposit{withdrawable.pendingDeposits.length - 3 === 1 ? '' : 's'} settling
              </Text>
            )}
            <Text style={styles.settlingNote}>
              Stripe holds new-merchant deposits for ~7 days before they can
              be withdrawn. You can still use settling funds to enter events
              — only external withdrawals wait for the clearing date.
            </Text>
          </View>
        )}

        {/* Bank Status */}
        <View style={styles.bankStatusCard}>
          <Ionicons name="checkmark-circle" size={24} color="#2ECC71" style={styles.bankStatusIcon} />
          <View style={styles.bankStatusContent}>
            <Text style={styles.bankStatusTitle}>Bank Connected</Text>
            <Text style={styles.bankStatusDesc}>Ready to receive withdrawals</Text>
          </View>
          <TouchableOpacity onPress={handleConnectBank}>
            <Text style={styles.manageText}>Manage</Text>
          </TouchableOpacity>
        </View>

        {/* Method Selector */}
        <Text style={styles.sectionTitle}>Withdrawal Method</Text>

        <TouchableOpacity
          style={[
            styles.methodOption,
            withdrawMethod === 'standard' && styles.methodOptionActive,
          ]}
          onPress={() => setWithdrawMethod('standard')}
        >
          <View style={styles.methodHeader}>
            <Text style={styles.methodTitle}>Standard Transfer</Text>
            <Text style={styles.methodFee}>FREE</Text>
          </View>
          <Text style={styles.methodDesc}>2-3 business days | ACH bank transfer</Text>
        </TouchableOpacity>
        
        {/* Turbo Pay (instant withdrawals). Visibility is controlled by
            settings/stripe.features.turboPay.enabled, surfaced to the client
            via eligibility.turboPayEnabled. When admin toggles the flag off
            (PaymentSettings dashboard), this block disappears and any in-
            flight selection silently falls back to standard via the safety
            useEffect below. createWithdrawal also hard-rejects method='turbo'
            when the flag is off, so a stale client can't sneak one through.
        */}
        {eligibility?.turboPayEnabled && (
          <>
            <TouchableOpacity
              style={[
                styles.methodOption,
                styles.methodOptionTurbo,
                withdrawMethod === 'turbo' && styles.methodOptionActive,
              ]}
              onPress={() => setWithdrawMethod('turbo')}
            >
              <View style={styles.methodHeader}>
                <View style={{flexDirection: "row", alignItems: "center"}}>
                  <Ionicons name="flash" size={18} color="#9B59B6" style={{marginRight: 4}} />
                  <Text style={styles.methodTitle}>Turbo Pay</Text>
                </View>
                {/* Live exact fee whenever the user has typed an amount; falls
                    back to the fee-schedule summary before they enter one.
                    No "*Fees apply" asterisk pattern — show the actual number
                    so they know exactly what this costs. */}
                <Text style={styles.methodFeeTurbo}>
                  {withdrawAmountCents > 0
                    ? `Fee: ${formatCurrency(calculateTurboFee(withdrawAmountCents))}`
                    : '$10 flat ≤$500 · 2% above'}
                </Text>
              </View>
              <Text style={styles.methodDesc}>~30 minutes | Instant to debit card</Text>
            </TouchableOpacity>

            {/* Hard warning when Turbo is selected but the connected
                destination doesn't support Stripe Instant Payouts (typically
                means user has only a standard bank account; needs a debit
                card OR an instant-payout-enabled bank for turbo to work). */}
            {withdrawMethod === 'turbo'
              && eligibility?.payoutDestination
              && eligibility.payoutDestination.instantEligible === false && (
              <View style={styles.turboWarning}>
                <Ionicons name="warning" size={18} color="#FCD000" style={{marginRight: 8}} />
                <Text style={styles.turboWarningText}>
                  Your connected {formatPayoutDestination(eligibility.payoutDestination)} doesn't support instant payouts.
                  Turbo Pay needs a debit card or an instant-eligible bank — add one in your bank settings,
                  or use Standard Transfer instead.
                </Text>
              </View>
            )}
          </>
        )}

        {/* Amount Input */}
        <Text style={styles.sectionTitle}>Amount</Text>

        <View style={styles.inputRow}>
          <Text style={styles.currencySymbol}>$</Text>
          <TextInput
            style={styles.amountInput}
            value={withdrawAmount}
            onChangeText={setWithdrawAmount}
            placeholder="0.00"
            placeholderTextColor={COLORS.DIM}
            keyboardType="decimal-pad"
          />
          <TouchableOpacity style={styles.maxButton} onPress={handleMaxWithdraw}>
            <Text style={styles.maxButtonText}>MAX</Text>
          </TouchableOpacity>
        </View>

        {/* Summary */}
        {withdrawAmountCents > 0 && (
          <View style={styles.summaryCard}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Withdrawal</Text>
              <Text style={styles.summaryValue}>{formatCurrency(withdrawAmountCents)}</Text>
            </View>
            {withdrawFeeCents > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Turbo Fee</Text>
                <Text style={[styles.summaryValue, styles.feeValue]}>
                  -{formatCurrency(withdrawFeeCents)}
                </Text>
              </View>
            )}
            <View style={[styles.summaryRow, styles.summaryTotal]}>
              <Text style={styles.summaryTotalLabel}>Total Deducted From Wallet</Text>
              <Text style={styles.summaryTotalValue}>
                {formatCurrency(withdrawTotalDeduction)}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>You Receive</Text>
              <Text style={styles.receiveValue}>{formatCurrency(withdrawAmountCents)}</Text>
            </View>
            {/* Destination + arrival expectations — pulled from getWithdrawalEligibility.
                Always shown so the user has zero ambiguity about where funds land. */}
            <View style={styles.summaryDivider} />
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Going to</Text>
              <Text style={styles.summaryDestValue}>
                {formatPayoutDestination(eligibility?.payoutDestination)}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Estimated arrival</Text>
              <Text style={styles.summaryDestValue}>
                {withdrawMethod === 'turbo'
                  ? 'Usually within 30 minutes'
                  : '2-7 business days'}
              </Text>
            </View>
            {/* New-platform settlement note — Stripe holds new-merchant payouts T+7 for
                the first ~30 days of processing history. After we request the payout
                schedule reduction (~2026-06-27), update copy back to "2-3 business days"
                and remove this note. See: reference_stripe_settlement_timing.md */}
            {withdrawMethod !== 'turbo' && (
              <View style={styles.summaryRow}>
                <Text style={[styles.summaryLabel, { fontSize: 11, fontStyle: 'italic', flex: 1, textAlign: 'right', color: '#888' }]}>
                  New platform — withdrawals speed up after our first month.
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Withdraw Button */}
        <TouchableOpacity
          style={[
            styles.actionButton,
            styles.withdrawButton,
            (withdrawSubmitting || withdrawAmountCents < 500 || withdrawTotalDeduction > withdrawableBalance) && styles.actionButtonDisabled,
          ]}
          onPress={handleWithdrawPress}
          disabled={withdrawSubmitting || withdrawAmountCents < 500 || withdrawTotalDeduction > withdrawableBalance}
        >
          {withdrawSubmitting ? (
            <ActivityIndicator color="#000" />
          ) : (
            <Text style={styles.actionButtonText}>
              {withdrawMethod === 'turbo' ? 'Turbo Withdraw' : 'Withdraw Funds'}
            </Text>
          )}
        </TouchableOpacity>
      </View>
    );
  };

  // ============================================================================
  // RENDER: TRANSACTIONS
  // ============================================================================

  // Merge wallet transactions + merch orders into a single chronological feed.
  // Merch entries are tagged with `kind: 'merch'` so the renderer can wrap
  // them in a TouchableOpacity that routes to the order history screen.
  const buildActivityFeed = () => {
    const txEntries = transactions.map((tx) => ({
      kind: 'wallet',
      id: tx.id || `tx-${tx.at || tx.createdAt}`,
      label: formatTransactionLabel(tx.reason, tx.type, tx.balanceType),
      dateValue: tx.at || tx.createdAt,
      amountCents: tx.amountCents,
      type: tx.type,
    }));

    const merchEntries = merchOrders.map((order) => {
      const sizeNote = order.size ? ` · ${order.size}` : '';
      return {
        kind: 'merch',
        id: `merch-${order.orderId}`,
        label: `Merch · ${order.productName || 'Order'}${sizeNote}`,
        dateValue: order.createdAt,
        amountCents: order.totalCents,
        type: 'debit',
        orderId: order.orderId,
      };
    });

    return [...txEntries, ...merchEntries].sort((a, b) => {
      // Newest first. Falsy dateValues sort to bottom.
      const aTs = a.dateValue ? new Date(a.dateValue).getTime() : 0;
      const bTs = b.dateValue ? new Date(b.dateValue).getTime() : 0;
      return bTs - aTs;
    });
  };

  const renderActivityItem = (entry) => {
    const body = (
      <>
        <View style={{ flex: 1 }}>
          <Text style={styles.txType}>{entry.label}</Text>
          <Text style={styles.txDate}>{formatDateTime(entry.dateValue)}</Text>
        </View>
        <Text style={[
          styles.txAmount,
          entry.type === 'credit' ? styles.txAmountCredit : styles.txAmountDebit,
        ]}>
          {entry.type === 'credit' ? '+' : '-'}{formatCurrency(entry.amountCents)}
        </Text>
      </>
    );

    if (entry.kind === 'merch') {
      return (
        <TouchableOpacity
          key={entry.id}
          style={styles.transactionItem}
          activeOpacity={0.7}
          onPress={() => navigation.navigate('MerchOrderHistory')}
        >
          {body}
        </TouchableOpacity>
      );
    }

    return (
      <View key={entry.id} style={styles.transactionItem}>
        {body}
      </View>
    );
  };

  const renderTransactions = () => {
    const feed = buildActivityFeed();

    return (
      <View style={styles.section}>
        <View style={styles.activityHeaderRow}>
          <Text style={styles.sectionTitle}>Recent Activity</Text>
          {merchOrders.length > 0 ? (
            <TouchableOpacity onPress={() => navigation.navigate('MerchOrderHistory')}>
              <Text style={styles.activityHeaderLink}>View all orders →</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        {transactionsLoading ? (
          <ActivityIndicator color={COLORS.GOLD} style={{ marginVertical: 20 }} />
        ) : feed.length === 0 ? (
          <Text style={styles.emptyText}>No transactions yet</Text>
        ) : (
          feed.map(renderActivityItem)
        )}
      </View>
    );
  };

  // ============================================================================
  // RENDER: MODALS
  // ============================================================================

  const renderDepositModal = () => {
    const amountCents = Math.round((parseFloat(depositAmount) || 0) * 100);

    return (
      <Modal visible={showDepositModal} transparent animationType="fade">
        <Pressable style={styles.modalBackdrop} onPress={() => setShowDepositModal(false)}>
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Confirm Deposit</Text>

            <View style={styles.modalAmountBox}>
              <Text style={styles.modalAmountLabel}>Amount to Deposit</Text>
              <Text style={styles.modalAmount}>{formatCurrency(amountCents)}</Text>
            </View>

            <View style={styles.modalInfoBox}>
              <View style={{flexDirection: "row", alignItems: "center", marginBottom: 8}}><Ionicons name="information-circle" size={18} color="#4a9eff" style={{marginRight: 6}} /><Text style={[styles.modalInfoTitle, {marginBottom: 0}]}>Before You Continue</Text></View>
              <Text style={styles.modalInfoText}>
                - You'll be redirected to Stripe's secure checkout
              </Text>
              <Text style={styles.modalInfoText}>
                - Funds will be added to your <Text style={styles.modalBold}>wallet balance</Text>
              </Text>
              <Text style={styles.modalInfoText}>
                - Wallet balance is withdrawable
              </Text>
            </View>

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalCancelBtn}
                onPress={() => setShowDepositModal(false)}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalConfirmBtn, { backgroundColor: COLORS.GOLD }]}
                onPress={confirmDeposit}
              >
                <Text style={styles.modalConfirmText}>Continue</Text>
              </TouchableOpacity>
            </View>

            <View style={{flexDirection: "row", alignItems: "center", justifyContent: "center"}}><Ionicons name="lock-closed" size={14} color="#666" style={{marginRight: 4}} /><Text style={styles.modalSecure}>Secure payment via Stripe</Text></View>
          </Pressable>
        </Pressable>
      </Modal>
    );
  };

  const renderBankModal = () => (
    <Modal visible={showBankModal} transparent animationType="fade">
      <Pressable style={styles.modalBackdrop} onPress={() => setShowBankModal(false)}>
        <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
          <Text style={styles.modalTitle}>Connect Bank Account</Text>

          <View style={styles.modalInfoBox}>
            <Text style={styles.modalInfoTitle}>About This Connection</Text>
            <Text style={styles.modalInfoText}>
              You will be redirected to Stripe, our payment processor, to securely connect your bank account. Your banking info is never stored on CritCash servers.
            </Text>
            <Text style={styles.modalInfoText}>
              Payouts appear as <Text style={styles.modalBold}>CritCash</Text> or <Text style={styles.modalBold}>Stripe</Text> on your bank statements.
            </Text>
          </View>

          <View style={styles.payoutCountryBox}>
            <Text style={styles.payoutCountryLabel}>Country where you live</Text>
            {Platform.OS === 'web' ? (
              <select
                value={payoutCountry}
                onChange={(e) => setPayoutCountry(e.target.value)}
                style={PAYOUT_COUNTRY_SELECT_STYLE}
                aria-label="Country where you live"
              >
                {COUNTRY_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>{opt.name}</option>
                ))}
              </select>
            ) : (
              <ScrollView style={styles.payoutCountryList} nestedScrollEnabled>
                {COUNTRY_OPTIONS.map((opt) => {
                  const selected = opt.code === payoutCountry;
                  return (
                    <TouchableOpacity
                      key={opt.code}
                      style={[styles.payoutCountryOption, selected && styles.payoutCountryOptionSelected]}
                      onPress={() => setPayoutCountry(opt.code)}
                    >
                      <Text style={[styles.payoutCountryOptionText, selected && styles.payoutCountryOptionTextSelected]}>
                        {opt.name}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            )}
            <Text style={styles.payoutCountryHelper}>Your payout account is set up for this country.</Text>
          </View>

          <View style={styles.modalButtons}>
            <TouchableOpacity
              style={styles.modalCancelBtn}
              onPress={() => setShowBankModal(false)}
            >
              <Text style={styles.modalCancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalConfirmBtn, { backgroundColor: COLORS.GREEN }]}
              onPress={confirmBankConnection}
            >
              <Text style={[styles.modalConfirmText, { color: '#fff' }]}>Connect Bank</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );

  const renderWithdrawModal = () => (
    <Modal visible={showWithdrawModal} transparent animationType="fade">
      <Pressable style={styles.modalBackdrop} onPress={() => setShowWithdrawModal(false)}>
        <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
          <Text style={styles.modalTitle}>
            {withdrawMethod === 'turbo' ? 'Confirm Turbo Withdrawal' : 'Confirm Withdrawal'}
          </Text>

          <View style={styles.modalAmountBox}>
            <Text style={styles.modalAmountLabel}>You Will Receive</Text>
            <Text style={[styles.modalAmount, { color: COLORS.GREEN }]}>
              {formatCurrency(withdrawAmountCents)}
            </Text>
            {withdrawFeeCents > 0 ? (
              <Text style={styles.modalFeeText}>
                Turbo fee: {formatCurrency(withdrawFeeCents)} · {formatCurrency(withdrawTotalDeduction)} leaves your wallet
              </Text>
            ) : (
              <Text style={styles.modalFeeText}>
                No fee · {formatCurrency(withdrawTotalDeduction)} leaves your wallet
              </Text>
            )}
          </View>

          <View style={styles.modalInfoBox}>
            <Text style={styles.modalInfoTitle}>
              {withdrawMethod === 'turbo' ? 'Turbo Pay' : 'Standard Transfer'}
            </Text>
            <Text style={styles.modalInfoText}>
              • Going to {formatPayoutDestination(eligibility?.payoutDestination)}
            </Text>
            <Text style={styles.modalInfoText}>
              {withdrawMethod === 'turbo'
                ? '• Arrives within ~30 minutes'
                : '• Arrives in 2-3 business days via ACH'
              }
            </Text>
            <Text style={styles.modalInfoText}>
              • Cannot be canceled once initiated
            </Text>
          </View>

          <View style={styles.modalButtons}>
            <TouchableOpacity
              style={styles.modalCancelBtn}
              onPress={() => setShowWithdrawModal(false)}
            >
              <Text style={styles.modalCancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.modalConfirmBtn,
                { backgroundColor: withdrawMethod === 'turbo' ? COLORS.TURBO : COLORS.GREEN },
              ]}
              onPress={confirmWithdraw}
            >
              <Text style={[styles.modalConfirmText, { color: '#fff' }]}>
                Withdraw
              </Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <View style={styles.container}>
      <Header navigation={navigation} />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={COLORS.GOLD}
          />
        }
      >
        {renderTaxBanner()}
        {renderBalance()}
        {renderCreatorMetrics()}
        {renderTabs()}

        {!WITHDRAWALS_ENABLED ? (
          /* No custody: the monetary-account view — automatic payouts. */
          renderPayoutAccount()
        ) : CASH_DEPOSITS_ENABLED && activeTab === 'deposit' ? renderDepositTab() : renderWithdrawTab()}

        {renderTransactions()}

        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
        >
          <View style={{flexDirection: "row", alignItems: "center"}}><Ionicons name="arrow-back" size={18} color="#888" style={{marginRight: 4}} /><Text style={styles.backButtonText}>Back</Text></View>
        </TouchableOpacity>
      </ScrollView>
      

      {renderDepositModal()}
      {renderBankModal()}
      {WITHDRAWALS_ENABLED && renderWithdrawModal()}

      <NavBar navigation={navigation} />
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.BG,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 100,
  },

  // Payout Account (monetary-account view — automatic payouts)
  payoutCard: {
    backgroundColor: COLORS.CARD,
    borderColor: COLORS.CARD_BORDER,
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  payoutRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  payoutRowTitle: {
    color: COLORS.TEXT,
    fontSize: 14,
    fontWeight: '700',
  },
  payoutRowBody: {
    color: '#aaa',
    fontSize: 13,
    lineHeight: 19,
    marginTop: 2,
  },
  payoutBankRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderColor: COLORS.GREEN,
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },
  payoutBankTitle: {
    color: COLORS.TEXT,
    fontSize: 14,
    fontWeight: '700',
  },
  payoutManageLink: {
    color: COLORS.GOLD,
    fontSize: 13,
    fontWeight: '700',
  },
  payoutSetupBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.GOLD,
    borderRadius: 10,
    paddingVertical: 13,
    marginBottom: 8,
  },
  payoutSetupBtnAmber: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f59e0b',
    borderRadius: 10,
    paddingVertical: 13,
    marginBottom: 12,
  },
  payoutSetupBtnText: {
    color: '#0b0b0b',
    fontSize: 15,
    fontWeight: '800',
  },
  payoutSetupHint: {
    color: COLORS.DIM,
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 12,
  },
  payoutTaxNote: {
    color: '#f59e0b',
    fontSize: 12,
    lineHeight: 18,
    marginBottom: 12,
  },
  payoutHistoryTitle: {
    color: COLORS.TEXT,
    fontSize: 15,
    fontWeight: '700',
    marginTop: 4,
    marginBottom: 8,
  },
  payoutStatusChip: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  payoutStatusChipText: {
    fontSize: 11,
    fontWeight: '700',
  },

  // Tax-compliance banners (IRS 1099-MISC)
  taxGateBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(239, 68, 68, 0.12)',
    borderColor: '#ef4444',
    borderWidth: 2,
    borderRadius: 8,
    padding: 14,
    marginBottom: 12,
  },
  taxGateBannerTitle: { color: '#ef4444', fontSize: 14, fontWeight: '700' },
  taxGateBannerBody: { color: '#ef4444', fontSize: 12, marginTop: 2 },

  taxWarnBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(245, 158, 11, 0.10)',
    borderColor: '#f59e0b',
    borderWidth: 1,
    borderRadius: 6,
    padding: 10,
    marginBottom: 12,
    gap: 8,
  },
  taxWarnBannerText: { color: '#f59e0b', fontSize: 12, flex: 1, lineHeight: 16 },

  taxInfoBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    borderColor: '#3b82f6',
    borderWidth: 1,
    borderRadius: 6,
    padding: 10,
    marginBottom: 12,
    gap: 8,
  },
  taxInfoBannerText: { color: '#3b82f6', fontSize: 12, flex: 1, lineHeight: 16 },

  // Balance Section
  balanceSection: {
    marginBottom: 20,
  },
  balanceCard: {
    backgroundColor: COLORS.CARD,
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
    marginBottom: 12,
  },

  // Crits card (spend currency — sits above the Earnings card)
  critCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: '#3a3000',
    marginBottom: 12,
    gap: 14,
  },
  critCardDie: {
    width: 54,
    height: 54,
  },
  critCardBody: {
    flex: 1,
  },
  critCardLabel: {
    color: COLORS.DIM,
    fontSize: 13,
    fontWeight: '600',
  },
  critCardValue: {
    color: COLORS.GOLD,
    fontSize: 28,
    fontWeight: '900',
    marginTop: 2,
  },
  critCardHint: {
    color: '#666',
    fontSize: 11,
    marginTop: 4,
  },
  getCritsBtn: {
    backgroundColor: COLORS.GOLD,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
  },
  getCritsBtnText: {
    color: '#000',
    fontSize: 14,
    fontWeight: '800',
  },
  balanceLabel: {
    fontSize: 14,
    color: COLORS.DIM,
    marginBottom: 4,
  },
  balanceAmount: {
    fontSize: 36,
    fontWeight: 'bold',
    color: COLORS.GOLD,
  },
  balanceBreakdown: {
    flexDirection: 'row',
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: COLORS.CARD_BORDER,
    gap: 20,
    flexWrap: 'wrap',
  },
  balanceGiftNote: {
    marginTop: 10,
    fontSize: 12,
    color: COLORS.DIM,
  },
  breakdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  breakdownDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  breakdownLabel: {
    fontSize: 13,
    color: COLORS.DIM,
  },
  breakdownValue: {
    fontSize: 13,
    fontWeight: '600',
  },

  // Gift Card Button
  giftCardButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: COLORS.GIFT,
  },
  giftCardIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  giftCardContent: {
    flex: 1,
  },
  giftCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.TEXT,
  },
  giftCardSubtitle: {
    fontSize: 12,
    color: COLORS.DIM,
    marginTop: 2,
  },
  giftCardArrow: {
    fontSize: 24,
    color: COLORS.GIFT,
  },

  // Tabs
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 4,
    marginBottom: 20,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 10,
  },
  tabActive: {
    backgroundColor: COLORS.GOLD,
  },
  tabText: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.DIM,
  },
  tabTextActive: {
    color: '#000',
  },

  // Section
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: COLORS.TEXT,
    marginBottom: 12,
  },

  // Creator metrics (this-month audience stats + sign-in nudge)
  metricsSection: {
    backgroundColor: COLORS.CARD,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
  },
  metricsHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  metricsFullLink: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1a1604',
    borderColor: '#4a3d00',
    borderWidth: 1,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginTop: 12,
  },
  metricsFullLinkText: {
    flex: 1,
    color: COLORS.GOLD,
    fontSize: 13,
    fontWeight: '700',
  },
  metricsMonth: {
    fontSize: 13,
    color: COLORS.DIM,
    fontWeight: '600',
  },
  metricsAudienceRow: {
    flexDirection: 'row',
    marginTop: 14,
    borderTopWidth: 1,
    borderTopColor: '#1e1e1e',
    paddingTop: 12,
  },
  metricsAudienceCell: {
    flex: 1,
    alignItems: 'center',
  },
  metricsAudienceValue: {
    color: COLORS.TEXT,
    fontSize: 16,
    fontWeight: '700',
  },
  metricsAudienceLabel: {
    color: COLORS.DIM,
    fontSize: 11,
    marginTop: 2,
  },
  metricsConversionBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(252, 208, 0, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(252, 208, 0, 0.35)',
    borderRadius: 8,
    padding: 10,
    marginTop: 12,
  },
  metricsConversionText: {
    color: COLORS.TEXT,
    fontSize: 12,
    lineHeight: 17,
    flex: 1,
  },
  metricsConversionCount: {
    color: COLORS.GOLD,
    fontWeight: '700',
  },
  activityHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  activityHeaderLink: {
    color: COLORS.GOLD,
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 12,
  },

  // Quick Amounts
  quickAmounts: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  quickAmountBtn: {
    flex: 1,
    backgroundColor: COLORS.CARD,
    borderRadius: 10,
    paddingVertical: 14,
    marginHorizontal: 4,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
  },
  quickAmountBtnActive: {
    backgroundColor: COLORS.GOLD,
    borderColor: COLORS.GOLD,
  },
  quickAmountText: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.TEXT,
  },
  quickAmountTextActive: {
    color: '#000',
  },

  // Input Row
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  currencySymbol: {
    fontSize: 28,
    color: COLORS.DIM,
    marginRight: 8,
  },
  amountInput: {
    flex: 1,
    fontSize: 32,
    fontWeight: 'bold',
    color: COLORS.TEXT,
    paddingVertical: 16,
  },
  maxButton: {
    backgroundColor: COLORS.GOLD,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  maxButtonText: {
    color: '#000',
    fontSize: 12,
    fontWeight: 'bold',
  },

  // Action Buttons
  actionButton: {
    borderRadius: 12,
    padding: 18,
    alignItems: 'center',
    marginTop: 8,
  },
  depositButton: {
    backgroundColor: COLORS.GOLD,
  },
  withdrawButton: {
    backgroundColor: COLORS.GREEN,
  },
  actionButtonDisabled: {
    backgroundColor: '#333',
  },
  actionButtonText: {
    color: '#000',
    fontSize: 18,
    fontWeight: 'bold',
  },
  hintText: {
    fontSize: 12,
    color: COLORS.DIM,
    textAlign: 'center',
    marginTop: 12,
  },

  // Withdrawable Card
  withdrawableCard: {
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
  },
  // Standalone settling card used between Withdrawable Balance and Bank
  // Status. Subtle amber border signals "in progress / waiting" without
  // looking like an error. Same internal layout as the inline
  // settlingBlock used in the no-bank-connected branch.
  settlingCard: {
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#3a2a1a',
    borderLeftWidth: 3,
    borderLeftColor: '#f39c12',
  },
  withdrawableLabel: {
    fontSize: 13,
    color: COLORS.DIM,
    marginBottom: 4,
  },
  withdrawableAmount: {
    fontSize: 28,
    fontWeight: 'bold',
    color: COLORS.GREEN,
  },
  turboCapHint: {
    fontSize: 12,
    color: COLORS.DIM,
    marginTop: 6,
    fontStyle: 'italic',
  },
  giftNote: {
    fontSize: 12,
    color: COLORS.GIFT,
    marginTop: 8,
  },

  // Funds-settling breakdown shown when there are pending deposits
  settlingBlock: {
    marginTop: 14,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: COLORS.CARD_BORDER,
  },
  settlingHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  settlingHeader: {
    fontSize: 12,
    color: COLORS.DIM,
    fontWeight: '600',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  settlingTotal: {
    fontSize: 14,
    color: COLORS.TEXT,
    fontWeight: '600',
  },
  settlingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
  },
  settlingRowAmt: {
    fontSize: 13,
    color: COLORS.TEXT,
  },
  settlingRowMeta: {
    fontSize: 13,
    color: COLORS.DIM,
  },
  settlingMore: {
    fontSize: 12,
    color: COLORS.DIM,
    marginTop: 4,
    fontStyle: 'italic',
  },
  settlingNote: {
    fontSize: 11,
    color: COLORS.DIM,
    marginTop: 10,
    fontStyle: 'italic',
    lineHeight: 15,
  },

  // Connect Bank Card
  connectBankCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 20,
    borderWidth: 2,
    borderColor: COLORS.GOLD,
    borderStyle: 'dashed',
  },
  connectBankIcon: {
    fontSize: 28,
    marginRight: 12,
  },
  connectBankContent: {
    flex: 1,
  },
  connectBankTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.GOLD,
  },
  connectBankDesc: {
    fontSize: 13,
    color: COLORS.DIM,
    marginTop: 2,
  },
  connectBankArrow: {
    fontSize: 24,
    color: COLORS.GOLD,
  },

  // Bank Status Card
  bankStatusCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0a2a0a',
    borderRadius: 12,
    padding: 14,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#1a4a1a',
  },
  bankStatusIcon: {
    fontSize: 18,
    color: COLORS.GREEN,
    marginRight: 12,
  },
  bankStatusContent: {
    flex: 1,
  },
  bankStatusTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.GREEN,
  },
  bankStatusDesc: {
    fontSize: 12,
    color: COLORS.DIM,
  },
  manageText: {
    fontSize: 14,
    color: COLORS.BLUE,
    fontWeight: '500',
  },

  // Warning Card
  warningCard: {
    backgroundColor: '#2a1a0a',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#4a2a0a',
  },
  warningTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.GOLD,
    marginBottom: 12,
  },
  warningItem: {
    fontSize: 14,
    color: COLORS.TEXT,
    marginBottom: 6,
  },
  verifyButton: {
    backgroundColor: COLORS.GOLD,
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginTop: 12,
  },
  verifyButtonText: {
    color: '#000',
    fontSize: 14,
    fontWeight: '600',
  },

  // Method Options
  methodOption: {
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: COLORS.CARD_BORDER,
  },
  methodOptionTurbo: {
    borderColor: COLORS.TURBO,
    borderWidth: 1,
  },
  methodOptionActive: {
    borderColor: COLORS.GOLD,
    backgroundColor: '#1a1a0a',
  },
  methodHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  methodTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: COLORS.TEXT,
  },
  methodFee: {
    fontSize: 14,
    fontWeight: 'bold',
    color: COLORS.GREEN,
  },
  methodFeeTurbo: {
    fontSize: 12,
    fontWeight: '600',
    color: COLORS.TURBO,
  },
  methodDesc: {
    fontSize: 13,
    color: COLORS.DIM,
  },
  feeExplainer: {
    fontSize: 11,
    color: COLORS.DIM,
    fontStyle: 'italic',
    marginBottom: 20,
  },

  // Inline warning shown when Turbo selected against ineligible destination
  turboWarning: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(252, 208, 0, 0.10)',
    borderColor: '#FCD000',
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
    marginBottom: 12,
  },
  turboWarningText: {
    flex: 1,
    color: '#FCD000',
    fontSize: 12,
    lineHeight: 17,
  },

  // Summary card divider for destination/arrival block
  summaryDivider: {
    height: 1,
    backgroundColor: COLORS.CARD_BORDER,
    marginVertical: 8,
  },
  summaryDestValue: {
    fontSize: 13,
    color: COLORS.TEXT,
    fontWeight: '500',
    textAlign: 'right',
    flexShrink: 1,
  },

  // Summary Card
  summaryCard: {
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  summaryTotal: {
    borderTopWidth: 1,
    borderTopColor: COLORS.CARD_BORDER,
    paddingTop: 8,
    marginTop: 4,
  },
  summaryLabel: {
    fontSize: 14,
    color: COLORS.DIM,
  },
  summaryValue: {
    fontSize: 14,
    color: COLORS.TEXT,
  },
  summaryTotalLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.TEXT,
  },
  summaryTotalValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: COLORS.GOLD,
  },
  feeValue: {
    color: COLORS.DANGER,
  },
  receiveValue: {
    fontSize: 14,
    color: COLORS.GREEN,
    fontWeight: 'bold',
  },

  // Transactions
  transactionItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: COLORS.CARD,
    borderRadius: 10,
    padding: 14,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
  },
  txType: {
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.TEXT,
  },
  txDate: {
    fontSize: 12,
    color: COLORS.DIM,
    marginTop: 2,
  },
  txAmount: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  txAmountCredit: {
    color: COLORS.GREEN,
  },
  txAmountDebit: {
    color: COLORS.DANGER,
  },
  emptyText: {
    color: COLORS.DIM,
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 20,
  },

  // Back Button
  backButton: {
    padding: 12,
    alignItems: 'center',
  },
  backButtonText: {
    color: COLORS.DIM,
    fontSize: 16,
  },

  // Modals
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#111',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    borderWidth: 1,
    borderColor: '#333',
  },
  modalTitle: {
    color: COLORS.GOLD,
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 20,
  },
  modalAmountBox: {
    backgroundColor: '#1a1a1a',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: 20,
  },
  modalAmountLabel: {
    color: COLORS.DIM,
    fontSize: 14,
    marginBottom: 4,
  },
  modalAmount: {
    color: COLORS.GOLD,
    fontSize: 32,
    fontWeight: '700',
  },
  modalFeeText: {
    color: COLORS.DIM,
    fontSize: 12,
    marginTop: 8,
  },
  modalInfoBox: {
    backgroundColor: '#0a1a2a',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#1a3a5a',
  },
  modalInfoTitle: {
    color: '#4a9eff',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  modalInfoText: {
    color: '#aaa',
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 6,
  },
  modalBold: {
    color: '#fff',
    fontWeight: '600',
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  modalCancelBtn: {
    flex: 1,
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#333',
    alignItems: 'center',
  },
  modalCancelText: {
    color: COLORS.DIM,
    fontSize: 16,
    fontWeight: '600',
  },
  modalConfirmBtn: {
    flex: 2,
    padding: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  modalConfirmText: {
    color: '#000',
    fontSize: 16,
    fontWeight: '700',
  },
  modalSecure: {
    color: '#666',
    fontSize: 12,
    textAlign: 'center',
  },

  // Payout country claim (Connect Bank modal, founder 2026-10-14)
  payoutCountryBox: {
    marginBottom: 20,
  },
  payoutCountryLabel: {
    color: COLORS.TEXT,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  payoutCountryHelper: {
    color: COLORS.DIM,
    fontSize: 12,
    marginTop: 8,
  },
  payoutCountryList: {
    maxHeight: 180,
    borderWidth: 1,
    borderColor: '#333',
    borderRadius: 8,
    backgroundColor: '#0a0a0a',
  },
  payoutCountryOption: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  payoutCountryOptionSelected: {
    backgroundColor: '#1a1a1a',
  },
  payoutCountryOptionText: {
    color: '#aaa',
    fontSize: 14,
  },
  payoutCountryOptionTextSelected: {
    color: COLORS.GOLD,
    fontWeight: '600',
  },
});